import React, { useEffect, useMemo, useState } from "react";
import { Panel, Dropdown, Loader } from "@culturehq/components";
import * as CHQ from "chq";

import { useFieldsLabels, useGet } from "app/hooks";
import { formatThousand, t, Trans } from "app/utils";

import { Params } from "@culturehq/client";
import ProgressLine from "./ProgressLine";
import getColorList from "./getColorList";

type SegmentChartProps = {
  segments: CHQ.RsvpSegment[];
};

const SegmentChart: React.FC<SegmentChartProps> = ({ segments }) => {
  const maxValue = Math.max(...segments.map(({ total }) => total));
  const colors = useMemo(() => getColorList(segments.length), [segments.length]);

  return (
    <>
      {segments.map((segment, index) => (
        <div key={segment.name} className="row_bar">
          <div className="info_label">{segment.name}</div>
          <ProgressLine
            color={colors[index]}
            valueNow={segment.total}
            valueMax={maxValue}
          />
          <div className="info_txt">{formatThousand(segment.total)}</div>
        </div>
      ))}
    </>
  );
};

type StorySegmentContentProps = {
  filter: string;
  external?: boolean;
  params?: Params;
};

export type ListStorySegments = {
  segments: CHQ.StorySegment[];
};

const StorySegmentContent: React.FC<StorySegmentContentProps> = ({ external, filter, params }) => {
  const get = useGet<ListStorySegments>(`/analytics/stories/segments/${filter}${external ? "?external=true" : ""}`, params);

  if (get.getting) {
    return <Loader loading />;
  }

  if (get.error) {
    throw new Error(t("Looks like there has been an unexpected error. Please reload the page."));
  }

  return (
    <div className="card_info">
      {
        get.got.segments.length > 0
          ? <SegmentChart segments={get.got.segments} />
          : <Trans>No data</Trans>
      }
    </div>
  );
};

type SegmentProps = {
  external?: boolean;
  params?: Params;
};

const OPTIONS = ["departments", "locations", "interests", "skills", "values"];
const firstAvailableOption = (params?: Params) => {
  if (!params) return OPTIONS[0];
  return OPTIONS.find(opt => !params[opt]);
};

const isDisabled = (option: any) => (option ? "disabled" : "");

const StorySegment: React.FC<SegmentProps> = ({ external, params }) => {
  const [filter, setFilter] = useState(() => firstAvailableOption(params));
  const labels = useFieldsLabels();

  useEffect(() => { setFilter(firstAvailableOption(params)); }, [params]);

  const handleChange = (value: string) => {
    if (params?.[value]) return;

    setFilter(value);
  };

  return (
    <Panel>
      <Panel.Body className="card_holder">
        <div className="card_title">
          <h3>
            {external
              ? <Trans>Top Viewed Segments</Trans>
              : <Trans>Top Segments Represented in Stories</Trans>}
          </h3>
          <Dropdown selected={filter as string} onChange={handleChange}>
            <Dropdown.Button>
              <Trans>Segment By</Trans>
            </Dropdown.Button>
            <Dropdown.ListBox className="custom_option">
              <Dropdown.Option value="departments" className={isDisabled(params?.departments)}>
                <Trans>{labels.departments.plural}</Trans>
              </Dropdown.Option>
              <Dropdown.Option value="locations" className={isDisabled(params?.locations)}>
                <Trans>Locations</Trans>
              </Dropdown.Option>
              <Dropdown.Option value="interests" className={isDisabled(params?.interests)}>
                <Trans>Interests</Trans>
              </Dropdown.Option>
              <Dropdown.Option value="skills" className={isDisabled(params?.skills)}>
                <Trans>Skills</Trans>
              </Dropdown.Option>
              <Dropdown.Option value="values" className={isDisabled(params?.values)}>
                <Trans>Values</Trans>
              </Dropdown.Option>
            </Dropdown.ListBox>
          </Dropdown>
        </div>
        <StorySegmentContent filter={filter as string} external={external} params={params} />
      </Panel.Body>
    </Panel>
  );
};

export default StorySegment;
